import { forwardRef, useContext } from "react";

import Box from "@/components/Box";
import CircleButton from "@/components/CircleButton";
import { CollapseableText } from "@/components/CollapseableText";
import { getAnswerColor } from "@/components/QuestionnaireReport";
import { SpanText } from "@/components/Text";
import CardListContext from "@/contexts/CardListContext";
import { CMP_SHOP_TAB_URL } from "@/core/urls";
import useCmpRelatedUrl from "@/hooks/organisations/useCmpRelatedUrl";
import BloodIcon from "@/images/BloodIcon";
import PenIcon from "@/images/PenIcon";
import SimpleDnaIcon from "@/images/SimpleDnaIcon";
import Accordion, { AccordionContext } from "@/tpo/Accordion";
import Group from "@/tpo/Group";
import Stack from "@/tpo/Stack";
import { mapDnaDatapointToSnpRow } from "@/utils/getSnpColor";
import { getInterpretationColor } from "@/utils/shared";
import ButtonV2 from "@/v2/Buttons";
import { useLazyQuery } from "@apollo/client";

import { Badge } from "./Badges";
import {
  SnpSection,
  InterpretationSection,
  PotentialSymptomsRow
} from "./InterpretationSharedRows";
import { InterpretationChart, Title } from "./MarkersAssessedChart";

const InterpretationCard = forwardRef(
  (
    {
      id,
      title,
      description,
      diagramName,
      onOpenDiagram,
      dnaDatapointCounts = {
        inRangeCount: 0,
        outOfRangeCount: 0,
        untestedCount: 0
      },
      functionalDatapointCounts = {
        inRangeCount: 0,
        outOfRangeCount: 0,
        untestedCount: 0
      },
      symptomCounts = {
        inRangeCount: 0,
        outOfRangeCount: 0,
        untestedCount: 0
      },
      query,
      queryVariables
    },
    ref
  ) => {
    const itemValue = `interpretation-card-${id}`;
    const [loadInterpretationDetails, { data: detailsData, loading: detailsLoading, called }] =
      useLazyQuery(query, {
        fetchPolicy: "cache-first"
      });

    const dnaDatapoints = detailsData?.userResult?.dnaDatapoints || [];
    const functionalDatapoints = detailsData?.userResult?.functionalDatapoints || [];
    const untestedDatapoints = detailsData?.userResult?.untestedDatapoints || [];
    const untestedDnaDatapoints = detailsData?.userResult?.untestedDnaDatapoints || [];

    const { resolvedUrls } = useCmpRelatedUrl();

    const snpRows = dnaDatapoints.map(mapDnaDatapointToSnpRow);
    const untestedSnpRows = untestedDnaDatapoints.map(datapoint => ({
      title: datapoint.name,
      accentColor: "midGrey",
      state: "untested",
    }));

    const ctx = useContext(CardListContext);

    const functionalRows = functionalDatapoints.map((datapoint, _idx) => ({
      title: datapoint?.datapoint?.name,
      sampleType: datapoint?.sampleType,
      dateExtracted: datapoint?.dateExtracted,
      accentColor: getInterpretationColor(datapoint?.chartConfiguration?.interpretation),
      badgeText: datapoint?.value,
      units: datapoint?.datapoint?.units,
      onClick: () => {
        ctx?.viewCard({
          type: "datapoints",
          item: datapoint
        });
      }
    }));

    const untestedMarkerRows = untestedDatapoints.map(datapoint => ({
      title: datapoint.name,
      productName: datapoint.advertisedTestProductName ? datapoint.advertisedTestProductName : null,
      productLink: datapoint.advertisedTestProductSlug
        ? `${resolvedUrls[CMP_SHOP_TAB_URL].replace(":tab", "tests")}?slug=${
            datapoint.advertisedTestProductSlug
          }`
        : null
    }));

    const potentialSymptomsRows = [...(detailsData?.userResult?.questionsAndAnswers || [])]
      ?.sort((a, b) => b.answerScore - a.answerScore)
      .map(q => ({
        title: q.question.symptomGroup,
        risk: q.answerText || "No Answer",
        color: q.answerScore !== null ? getAnswerColor(q.answerScore) : "midGrey"
      }));

    const snpAssessedCount = dnaDatapointCounts.inRangeCount + dnaDatapointCounts.outOfRangeCount;
    const snpTotalCount = snpAssessedCount + dnaDatapointCounts.untestedCount;
    const functionalAssessedCount =
      functionalDatapointCounts.inRangeCount + functionalDatapointCounts.outOfRangeCount;
    const functionalTotalCount = functionalAssessedCount + functionalDatapointCounts.untestedCount;
    const markersAssessedCount = snpAssessedCount + functionalAssessedCount;
    const markersTotalCount = snpTotalCount + functionalTotalCount;
    const symptomsAssessedCount = symptomCounts.inRangeCount + symptomCounts.outOfRangeCount;
    const symptomsTotalCount = symptomsAssessedCount + symptomCounts.untestedCount;

    return (
      <Accordion variant="default" size={0} ref={ref}>
        <Accordion.Item value={itemValue}>
          <AccordionContext.Consumer>
            {({ openState, open, close }) => {
              const isOpen = openState.has(itemValue);
              const handleToggleOpen = () => {
                if (!isOpen && !called) {
                  loadInterpretationDetails({
                    variables: {
                      userResultId: id,
                      ...queryVariables
                    }
                  });
                }

                if (isOpen) {
                  close(itemValue);
                } else {
                  open(itemValue);
                }
              };

              return (
                <>
                  <Stack
                    bg="white"
                    borderRadius={20}
                    borderBottomRightRadius={isOpen ? 0 : 20}
                    borderBottomLeftRadius={isOpen ? 0 : 20}
                    pt={[40, 40, 60]}
                    gap={40}
                    pb={isOpen ? 0 : [60, 60, 80]}
                    px={[24, 24, 40]}
                  >
                    <Stack gap={16} pr={[0, 0, 48]}>
                      <Box
                        fontFamily="gilroyBold"
                        fontSize={[32, 32, 44]}
                        lineHeight={["36px", "36px", "50px"]}
                      >
                        {title}
                      </Box>
                      <CollapseableText
                        richText
                        maxLines={8}
                        fontFamily="gilroyMedium"
                        fontSize={[14, 14, 16]}
                        lineHeight={1.75}
                      >
                        {description}
                      </CollapseableText>
                    </Stack>

                    {diagramName ? (
                      <ButtonV2
                        size="xs"
                        color="dark"
                        sx={{ width: "fit-content", mt: -40 + 28 }}
                        onClick={() => onOpenDiagram?.({ id, title, diagramName })}
                      >
                        Diagram
                      </ButtonV2>
                    ) : null}
                    <Stack gap={16}>
                      <Title assessed={markersAssessedCount} total={markersTotalCount} />
                      <Group gap={16} width="100%" flexDirection={["column", "column", "row"]}>
                        <InterpretationChart
                          sx={{ flexGrow: 1, minWidth: 0 }}
                          assessed={snpAssessedCount}
                          total={snpTotalCount}
                          inRange={dnaDatapointCounts.inRangeCount}
                          outOfRange={dnaDatapointCounts.outOfRangeCount}
                          untested={dnaDatapointCounts.untestedCount}
                          label="SNPs"
                          icon={<SimpleDnaIcon size={20} withCircle={false} color="black" />}
                          inRangeLabel="Reference:"
                          outOfRangeLabel="Variant:"
                        />
                        <InterpretationChart
                          sx={{ flexGrow: 1, minWidth: 0 }}
                          assessed={functionalAssessedCount}
                          total={functionalTotalCount}
                          inRange={functionalDatapointCounts.inRangeCount}
                          outOfRange={functionalDatapointCounts.outOfRangeCount}
                          untested={functionalDatapointCounts.untestedCount}
                          label="FUNCTIONAL"
                          icon={<BloodIcon size={20} withCircle={false} color="black" />}
                        />
                        <InterpretationChart
                          sx={{ flexGrow: 1, minWidth: 0 }}
                          assessed={symptomsAssessedCount}
                          total={symptomsTotalCount}
                          inRange={symptomCounts.inRangeCount}
                          outOfRange={symptomCounts.outOfRangeCount}
                          untested={symptomCounts.untestedCount}
                          label="SYMPTOMS"
                          icon={<PenIcon size={20} withCircle={false} color="black" />}
                        />
                      </Group>
                    </Stack>

                    <Group alignItems="center" ml="auto" gap={8}>
                      <SpanText
                        fontFamily="gilroyMedium"
                        fontSize={[14, 14, 16]}
                        lineHeight={["24px", "24px", "28px"]}
                      >
                        {isOpen ? "Hide results" : "Show results"}
                      </SpanText>
                      <CircleButton
                        direction={isOpen ? "top" : "bottom"}
                        iconWidth={10}
                        iconHeight={10}
                        onClick={handleToggleOpen}
                        arrowColor="white"
                        sx={{ bg: "dark" }}
                      />
                    </Group>
                  </Stack>
                  <Accordion.Panel
                    p={40}
                    pt={40}
                    pb={80}
                    px={[24, 24, 40]}
                    bg="white"
                    borderRadius={20}
                  >
                    <Stack gap={48}>
                      {detailsLoading ? (
                        <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="24px">
                          Loading marker details...
                        </Box>
                      ) : (
                        <>
                          <SnpSection testedRows={snpRows} untestedRows={untestedSnpRows} />
                          <InterpretationSection
                            label="Functional markers"
                            testedRows={functionalRows}
                            untestedRows={untestedMarkerRows}
                          />
                        </>
                      )}
                      {potentialSymptomsRows?.length ? (
                        <Stack gap={10}>
                          <Badge mr="auto">Potential symptoms</Badge>
                          <Stack gap={0}>
                            {potentialSymptomsRows?.map((row, idx) => (
                              <PotentialSymptomsRow
                                title={row.title}
                                risk={row.risk}
                                color={row.color}
                                key={`symptom-${idx}`}
                              />
                            ))}
                          </Stack>
                        </Stack>
                      ) : null}
                    </Stack>
                  </Accordion.Panel>
                </>
              );
            }}
          </AccordionContext.Consumer>
        </Accordion.Item>
      </Accordion>
    );
  }
);

export default InterpretationCard;
